import React, { useState } from "react";
import { GraduationCap, FileUp } from "lucide-react";
import coursesIntroVideo from "@/assets/shattaraai_courses_intro.mp4";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { CreateCourseWizard } from "@/components/courses/CreateCourseWizard";

export const DashboardHeroMastery: React.FC = () => {
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardInitialStep, setWizardInitialStep] = useState(0);

  const openWizard = (step: number) => {
    setWizardInitialStep(step);
    setWizardOpen(true);
  };

  return (
    <div className="flex w-full flex-col items-center">
      <div className="mb-5 flex flex-col items-center gap-2">
        <h2 className="text-xl text-center sm:text-3xl font-medium xl:text-4xl">
          What do you want to master?
        </h2>
        <p className="text-base text-muted-foreground text-center">
          Create a personalized course by chatting with AI.{" "}
          <Popover open={learnMoreOpen} onOpenChange={setLearnMoreOpen}>
            <PopoverTrigger asChild>
              <button className="inline-flex cursor-pointer items-center gap-1 underline underline-offset-4 text-muted-foreground transition-colors hover:text-foreground">
                Learn more
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-arrow-up-right h-3.5 w-3.5"
                  aria-hidden="true"
                >
                  <path d="M7 7h10v10" />
                  <path d="M7 17 17 7" />
                </svg>
              </button>
            </PopoverTrigger>
            <PopoverContent align="center" sideOffset={8} className="w-[calc(100vw-2rem)] sm:w-[440px] rounded-2xl p-5 leading-relaxed">
              <div className="mb-1 flex items-start justify-between">
                <h4 className="flex items-center gap-2 font-medium whitespace-nowrap">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-correct" />
                  Test your knowledge on everything
                </h4>
              </div>
              <p className="mb-4 text-sm text-muted-foreground">
                Build custom courses, get lesson, concept, and question breakdowns, track your progress, and review weak spots.
              </p>
              <div className="mb-3">
                <video
                  src={coursesIntroVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="h-auto w-full rounded-xl border border-border object-cover"
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setLearnMoreOpen(false)}
                  className="inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 rounded-lg px-3 text-sm"
                >
                  Got it!
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </p>
      </div>

      {/* Two CTA Cards */}
      <div className="z-10 w-full text-center md:max-w-lg xl:max-w-2xl flex flex-col">
        <div className="grid w-full grid-cols-1 gap-3 sm:flex sm:items-center sm:justify-center">
          {/* Create Course */}
          <div className="w-full flex-1 sm:w-1/2">
            <div
              onClick={() => openWizard(0)}
              className="group relative cursor-pointer rounded-3xl border border-border bg-card text-card-foreground shadow-sm transition-all duration-200 hover:shadow-md"
            >
              <div className="flex h-[112px] flex-col items-start justify-center gap-y-1 px-5 py-4">
                <div className="flex items-center gap-x-3 space-y-2 sm:block">
                  <GraduationCap className="mb-2 h-6 w-6 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
                  <div className="flex flex-col justify-center text-left">
                    <h3 className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground sm:text-base">
                      Create Course
                    </h3>
                    <p className="text-xs text-left text-muted-foreground/70 transition-colors group-hover:text-muted-foreground sm:text-sm">
                      Describe what you want to master
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Import Course */}
          <div className="w-full flex-1 sm:w-1/2">
            <div
              onClick={() => openWizard(1)}
              className="group relative cursor-pointer rounded-3xl border border-border bg-card text-card-foreground shadow-sm transition-all duration-200 hover:shadow-md"
            >
              <div className="flex h-[112px] flex-col items-start justify-center gap-y-1 px-5 py-4">
                <div className="flex items-center gap-x-3 space-y-2 sm:block">
                  <FileUp className="mb-2 h-6 w-6 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
                  <div className="flex flex-col justify-center text-left">
                    <h3 className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground sm:text-base">
                      Import Course
                    </h3>
                    <p className="text-xs text-left text-muted-foreground/70 transition-colors group-hover:text-muted-foreground sm:text-sm">
                      Upload PDFs, audio, or Youtube URLs
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateCourseWizard
        isOpen={wizardOpen}
        onOpenChange={setWizardOpen}
        initialStep={wizardInitialStep}
      />
    </div>
  );
};
